import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getSavedVideos,
  saveVideo,
  unsaveVideo,
} from '../../api/lfApiSavedVideos';

// useSavedVideoList owns the list-of-saved-videos state for callers
// that need to render save/unsave UI across many videos at once
// (e.g. the Feed card showing per-thumbnail heart buttons), as
// opposed to useSavedVideo which is the single-video variant the
// VideoPage uses.
//
// Returns:
//   savedVideos      — raw array; the order matches the API's most-
//                      recent-first list.
//   savedVideoIds    — Set<string> for O(1) "is this video saved?"
//                      lookups in render.
//   handleSaveToggle — toggles save/unsave for the given video and
//                      optimistically updates the local list so the
//                      heart icon flips without a refetch.
export function useSavedVideoList({ setError }) {
  const [savedVideos, setSavedVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const savedVideoIds = useMemo(
    () => new Set(savedVideos.map((item) => item.video_id)),
    [savedVideos],
  );

  const loadSavedVideos = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getSavedVideos(setError);
      if (response?.data?.items) {
        setSavedVideos(response.data.items);
      }
    } catch (error) {
      console.error('Error loading saved videos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    loadSavedVideos();
  }, [loadSavedVideos]);

  const handleSaveToggle = useCallback(
    async (video) => {
      const videoId = video?.id?.videoId || video?.videoId || video?.video_id;
      if (!videoId) return;

      if (savedVideoIds.has(videoId)) {
        const response = await unsaveVideo(videoId, setError);
        if (response) {
          setSavedVideos((prev) => prev.filter((item) => item.video_id !== videoId));
        }
        return;
      }

      const response = await saveVideo(video, setError);
      if (response?.data?.saved) {
        setSavedVideos((prev) => [
          response.data.saved,
          ...prev.filter((item) => item.video_id !== videoId),
        ]);
      }
    },
    [savedVideoIds, setError],
  );

  return { savedVideos, savedVideoIds, isLoading, handleSaveToggle };
}
